import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DataTable = ({ 
  title, 
  columns, 
  data, 
  onExport,
  pageSize = 10 
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const totalPages = Math.ceil(data?.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = React.useMemo(() => {
    if (!sortConfig?.key) return data;

    return [...data]?.sort((a, b) => {
      const aValue = a?.[sortConfig?.key];
      const bValue = b?.[sortConfig?.key];

      if (aValue < bValue) {
        return sortConfig?.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig?.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [data, sortConfig]);

  const paginatedData = sortedData?.slice(startIndex, endIndex);

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <div className="flex items-center justify-between p-18 md:p-24 border-b border-border">
        <h3 className="font-heading font-semibold text-base md:text-lg text-foreground">
          {title}
        </h3>
        {onExport && (
          <Button
            variant="outline"
            size="sm"
            onClick={onExport}
            iconName="Download"
            iconPosition="left"
          >
            Export
          </Button>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[600px]">
          <thead className="bg-muted">
            <tr>
              {columns?.map((column) => (
                <th
                  key={column?.key}
                  className={`
                    px-18 md:px-24 py-12 md:py-18 text-left
                    font-caption font-semibold text-[12px] md:text-[13px] text-muted-foreground uppercase tracking-wide
                    ${column?.sortable ? 'cursor-pointer hover:bg-muted/80 transition-smooth' : ''}
                  `}
                  onClick={() => column?.sortable && handleSort(column?.key)}
                >
                  <div className="flex items-center gap-8">
                    <span>{column?.label}</span>
                    {column?.sortable && (
                      <Icon
                        name={
                          sortConfig?.key === column?.key
                            ? sortConfig?.direction === 'asc' ?'ChevronUp' :'ChevronDown' :'ChevronsUpDown'
                        }
                        size={14}
                        color="var(--color-muted-foreground)"
                        strokeWidth={2}
                      />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData?.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className="border-b border-border last:border-b-0 hover:bg-muted/50 transition-smooth"
              >
                {columns?.map((column) => (
                  <td
                    key={column?.key}
                    className="px-18 md:px-24 py-12 md:py-18 font-body text-[13px] md:text-[14px] text-foreground"
                  >
                    {column?.render ? column?.render(row?.[column?.key], row) : row?.[column?.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between p-18 md:p-24 border-t border-border">
          <p className="font-caption text-[12px] md:text-[13px] text-muted-foreground">
            Showing {startIndex + 1} to {Math.min(endIndex, data?.length)} of {data?.length} entries
          </p>
          <div className="flex items-center gap-8">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              iconName="ChevronLeft"
              iconSize={16}
            >
              <span className="sr-only">Previous page</span>
            </Button>
            <span className="font-body text-[13px] md:text-[14px] text-foreground px-12">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              iconName="ChevronRight"
              iconSize={16}
            >
              <span className="sr-only">Next page</span>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;